'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, ChevronDown, Check, Globe, FolderTree, MapPin } from 'lucide-react';
import {
  useFacilityStore,
  FacilitySummary,
  type ServiceAreaSummary,
} from '../stores/facility-store';
import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import { fetchUserFacilities, fetchUserServiceAreas } from '../lib/supabase/facility-scope-queries';
import { createClient } from '../lib/supabase/client';
import { hasAnyRole } from '../lib/rbac';
import { cn, controlSizeClassName } from '@statura/ui';
import { useOrgLabels } from '../hooks/useOrgLabels';
import { formatUiLabel } from '@/lib/format';

const FACILITY_TYPE_LABELS: Record<string, string> = {
  RESIDENTIAL: 'Residential',
  HOME_CARE_OFFICE: 'Home care',
  SERVICE_REGION: 'Service region',
  DAY_CENTRE: 'Day centre',
  RESPITE_CENTRE: 'Respite',
  RETIREMENT_VILLAGE: 'Retirement village',
  HEAD_OFFICE: 'Head office',
};

interface FacilityRegion {
  id: string;
  name: string;
  parent_id: string | null;
  sort_order: number;
}

interface FacilitySwitcherProps {
  placement?: 'sidebar' | 'header';
  className?: string;
  includeServiceAreas?: boolean;
}

const SELECTABLE_FACILITY_STATUSES = new Set(['ACTIVE', 'SETUP']);
const SELECTABLE_SERVICE_AREA_STATUSES = new Set(['ACTIVE', 'SETUP', 'FULL']);

function isSelectableFacility(facility: FacilitySummary) {
  return SELECTABLE_FACILITY_STATUSES.has(facility.status);
}

function isSelectableServiceArea(serviceArea: ServiceAreaSummary) {
  return SELECTABLE_SERVICE_AREA_STATUSES.has(serviceArea.status);
}

function FacilityButton({
  facility,
  isSelected,
  onSelect,
  placement = 'sidebar',
}: {
  facility: FacilitySummary;
  isSelected: boolean;
  onSelect: () => void;
  placement?: FacilitySwitcherProps['placement'];
}) {
  const isHeader = placement === 'header';

  return (
    <button
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-2 text-left text-body transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:-outline-offset-2',
        isHeader
          ? isSelected
            ? 'bg-statura-green/15 text-slate-950'
            : 'text-slate-700 hover:bg-slate-100'
          : isSelected
            ? 'bg-statura-green/10 text-white'
            : 'text-slate-300 hover:bg-slate-700/50',
      )}
    >
      <Building2 className="h-4 w-4 shrink-0 text-text-subtle" />
      <div className="flex-1 min-w-0">
        <p className="truncate font-medium">{facility.name}</p>
        <p
          className={cn(
            'truncate text-caption',
            isHeader ? 'text-text-muted leading-tight' : 'text-slate-400',
          )}
        >
          {FACILITY_TYPE_LABELS[facility.facilityType] ?? formatUiLabel(facility.facilityType)}
          {' · '}
          {[facility.suburb, facility.state].filter(Boolean).join(', ')}
        </p>
      </div>
      {isSelected && <Check className="h-4 w-4 shrink-0 text-statura-green" />}
    </button>
  );
}

function ServiceAreaButton({
  serviceArea,
  isSelected,
  onSelect,
  placement = 'sidebar',
}: {
  serviceArea: ServiceAreaSummary;
  isSelected: boolean;
  onSelect: () => void;
  placement?: FacilitySwitcherProps['placement'];
}) {
  const isHeader = placement === 'header';

  return (
    <button
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-2 text-left text-body transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:-outline-offset-2',
        isHeader
          ? isSelected
            ? 'bg-statura-green/15 text-slate-950'
            : 'text-slate-700 hover:bg-slate-100'
          : isSelected
            ? 'bg-statura-green/10 text-white'
            : 'text-slate-300 hover:bg-slate-700/50',
      )}
    >
      <MapPin className="h-4 w-4 shrink-0 text-text-subtle" />
      <div className="flex-1 min-w-0">
        <p className="truncate font-medium">{serviceArea.name}</p>
        <p
          className={cn(
            'truncate text-caption',
            isHeader ? 'text-text-muted leading-tight' : 'text-slate-400',
          )}
        >
          {['SAH service area', serviceArea.facilityName].filter(Boolean).join(' · ')}
        </p>
      </div>
      {isSelected && <Check className="h-4 w-4 shrink-0 text-statura-green" />}
    </button>
  );
}

export function FacilitySwitcher({
  placement = 'sidebar',
  className,
  includeServiceAreas = true,
}: FacilitySwitcherProps = {}) {
  const [open, setOpen] = useState(false);
  const [regions, setRegions] = useState<FacilityRegion[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const user = useResolvedAuthUser();
  const tenantId = user?.tenantId;
  const userId = user?.id;
  const role = user?.role;
  const currentFacility = useFacilityStore((s) => s.currentFacility);
  const currentServiceArea = useFacilityStore((s) => s.currentServiceArea);
  const facilities = useFacilityStore((s) => s.facilities);
  const serviceAreas = useFacilityStore((s) => s.serviceAreas);
  const isLoaded = useFacilityStore((s) => s.isLoaded);
  const setFacilities = useFacilityStore((s) => s.setFacilities);
  const setServiceAreas = useFacilityStore((s) => s.setServiceAreas);
  const setAssignedFacilityIds = useFacilityStore((s) => s.setAssignedFacilityIds);
  const setAssignedServiceAreaIds = useFacilityStore((s) => s.setAssignedServiceAreaIds);
  const selectFacility = useFacilityStore((s) => s.selectFacility);
  const selectServiceArea = useFacilityStore((s) => s.selectServiceArea);
  const { locations } = useOrgLabels();
  const isHeader = placement === 'header';

  // Load facilities scoped to user's access level
  useEffect(() => {
    if (!tenantId || !userId || !role) return;
    Promise.all([
      fetchUserFacilities(tenantId, userId, role).catch(() => []),
      includeServiceAreas
        ? fetchUserServiceAreas(tenantId, userId, role).catch(() => [])
        : Promise.resolve([]),
    ])
      .then(([facilityData, serviceAreaData]) => {
        const nextFacilities = facilityData as FacilitySummary[];
        const nextServiceAreas = serviceAreaData as ServiceAreaSummary[];
        const hasUnrestrictedScope = hasAnyRole(role, ['SYSTEM_ADMIN', 'TENANT_ADMIN']);
        setServiceAreas(nextServiceAreas);
        setAssignedServiceAreaIds(
          hasUnrestrictedScope ? [] : nextServiceAreas.map((area) => area.id),
        );
        setFacilities(nextFacilities);
        setAssignedFacilityIds(
          hasUnrestrictedScope ? [] : nextFacilities.map((facility) => facility.id),
        );
      })
      .catch(() => {
        // Fail silently — single-location tenants may not have data yet
      });

    // Load regions for grouped display
    (async () => {
      try {
        const sb = createClient();
        const { data } = await sb
          .from('facility_regions')
          .select('id, name, parent_id, sort_order')
          .eq('tenant_id', tenantId)
          .order('sort_order');

        if (data && data.length > 0) {
          setRegions(data as FacilityRegion[]);
        }
      } catch {
        // Fail silently — regions are optional
      }
    })();
  }, [
    tenantId,
    userId,
    role,
    setFacilities,
    setServiceAreas,
    setAssignedFacilityIds,
    setAssignedServiceAreaIds,
    includeServiceAreas,
  ]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectableFacilities = useMemo(() => facilities.filter(isSelectableFacility), [facilities]);
  const selectableServiceAreas = useMemo(
    () => serviceAreas.filter(isSelectableServiceArea),
    [serviceAreas],
  );
  const selectableScopeCount = selectableFacilities.length + selectableServiceAreas.length;

  // Admins can always return to tenant-wide view. Non-admin users with multiple
  // assigned scopes can return to their assigned portfolio.
  const isAdmin = hasAnyRole(role, ['SYSTEM_ADMIN', 'TENANT_ADMIN']);
  const showAllOption = isAdmin || selectableScopeCount > 1;
  const canOpen = showAllOption || selectableScopeCount > 1;

  // Single-RAC-facility tenants still need a concrete facility context for
  // facility-scoped pages such as configuration, rooms, handovers, and QI entry.
  // Mixed RAC/SAH tenants must not be forced into the only RAC facility.
  useEffect(() => {
    if (
      !isLoaded ||
      showAllOption ||
      selectableScopeCount !== 1 ||
      selectableFacilities.length !== 1
    ) {
      return;
    }
    const onlyFacility = selectableFacilities[0];
    if (!onlyFacility) return;
    if (currentFacility?.id !== onlyFacility.id) {
      selectFacility(onlyFacility);
    }
  }, [
    currentFacility?.id,
    isLoaded,
    selectableFacilities,
    selectableScopeCount,
    selectFacility,
    showAllOption,
  ]);

  if (isLoaded && selectableScopeCount === 0) {
    return null;
  }

  // Lowercase only built-in vocabulary; organisation-supplied labels retain their spelling.
  const builtInLocations = ['Facilities', 'Offices', 'Service locations'].includes(locations);
  const inlineLocations = builtInLocations ? locations.toLowerCase() : locations;
  const allLabel = isAdmin
    ? `All ${builtInLocations ? locations.toLowerCase() : locations}`
    : `All my ${builtInLocations ? locations.toLowerCase() : locations}`;
  const allSubLabel = isAdmin ? 'Whole organisation' : 'Across assigned locations';
  const effectiveCurrentFacility =
    currentFacility ??
    (!showAllOption && selectableScopeCount === 1 ? selectableFacilities[0] : null);

  const displayName =
    currentServiceArea?.name ??
    effectiveCurrentFacility?.name ??
    (!isLoaded ? `Loading ${inlineLocations}` : allLabel);
  const displayType = currentServiceArea
    ? ['SAH service area', currentServiceArea.facilityName].filter(Boolean).join(' · ')
    : effectiveCurrentFacility
      ? (FACILITY_TYPE_LABELS[effectiveCurrentFacility.facilityType] ??
        formatUiLabel(effectiveCurrentFacility.facilityType))
      : allSubLabel;

  const hasRegions = regions.length > 0;

  // Build grouped view: region -> facilities
  const regionMap = new Map<string, FacilityRegion>();
  for (const r of regions) {
    regionMap.set(r.id, r);
  }

  // Group facilities by regionId
  const facilitiesByRegion = new Map<string | null, FacilitySummary[]>();
  for (const f of selectableFacilities) {
    const key = f.regionId ?? null;
    if (!facilitiesByRegion.has(key)) facilitiesByRegion.set(key, []);
    facilitiesByRegion.get(key)!.push(f);
  }

  // Sorted region IDs (by sort_order), then ungrouped at the end
  const sortedRegionIds = regions
    .filter((r) => !r.parent_id) // Top-level regions only for now
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((r) => r.id);
  const DisplayIcon = currentServiceArea ? MapPin : Building2;

  return (
    <div
      ref={ref}
      className={cn(
        'relative z-40',
        isHeader ? 'min-w-0 w-full max-w-[18rem] md:max-w-[22rem]' : 'mb-2 shrink-0 px-3',
        className,
      )}
    >
      <button
        onClick={() => canOpen && setOpen((value) => !value)}
        disabled={!canOpen}
        className={cn(
          'flex min-w-0 max-w-full w-full items-center gap-2 rounded-control border px-3 py-2 text-left transition-colors focus:outline focus:outline-2 focus:outline-focus focus:-outline-offset-2',
          isHeader && controlSizeClassName('toolbar'),
          isHeader && '!py-0',
          isHeader
            ? 'border-control-border/30 bg-surface-sheet text-text-heading hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-white'
            : 'border-slate-600/50 bg-slate-800/50 hover:bg-slate-700/50 disabled:cursor-default disabled:hover:bg-slate-800/50',
        )}
        aria-label={`${canOpen ? 'Switch facility' : 'Current facility'}: ${displayName}, ${displayType}`}
        aria-expanded={canOpen ? open : undefined}
      >
        <DisplayIcon className="h-4 w-4 shrink-0 text-statura-green" />
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'truncate text-sm font-medium',
              isHeader ? 'text-text-heading leading-tight' : 'text-white',
            )}
          >
            {displayName}
          </p>
          <p
            className={cn(
              'truncate text-caption',
              isHeader ? 'text-text-muted leading-tight' : 'text-slate-400',
            )}
          >
            {displayType}
          </p>
        </div>
        {canOpen ? (
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-slate-400 transition-transform',
              open && 'rotate-180',
            )}
          />
        ) : (
          <Check className="h-4 w-4 shrink-0 text-statura-green" />
        )}
      </button>

      {open && canOpen && (
        <div
          className={cn(
            'absolute top-full z-[80] mt-1 max-h-[min(24rem,calc(100vh-8rem))] overflow-y-auto rounded-overlay border shadow-popover',
            /*
              The header panel used to be pinned to the trigger with
              `left-0 right-0`, so it inherited the trigger's narrow width and
              ellipsised its own options: "Medea Park R…" over "Residential ·
              St Helen…". A facility switcher whose facility names do not fit is
              not a switcher. Observed on production 2026-08-26.

              It now starts at the trigger's left edge, grows to its content and
              stops at the smaller of 22rem and the viewport, so a long name reads
              in full and a very long one still cannot push off screen. The
              sidebar variant keeps left-3/right-3: it sits in a fixed-width rail
              where matching that width is the correct behaviour.
            */
            isHeader
              ? 'left-0 w-max min-w-full max-w-[min(22rem,calc(100vw-2rem))] border-slate-200 bg-white'
              : 'left-3 right-3 border-slate-600/50 bg-statura-ink',
          )}
        >
          {showAllOption && (
            <button
              onClick={() => {
                selectFacility(null);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-body transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus focus-visible:-outline-offset-2',
                isHeader
                  ? !currentFacility && !currentServiceArea
                    ? 'bg-statura-green/15 text-slate-950'
                    : 'text-slate-700 hover:bg-slate-100'
                  : !currentFacility && !currentServiceArea
                    ? 'bg-statura-green/10 text-white'
                    : 'text-slate-300 hover:bg-slate-700/50',
              )}
            >
              <Globe className="h-4 w-4 shrink-0 text-text-subtle" />
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium">{allLabel}</p>
                <p
                  className={cn(
                    'truncate text-caption',
                    isHeader ? 'text-text-muted leading-tight' : 'text-slate-400',
                  )}
                >
                  {allSubLabel}
                </p>
              </div>
              {!currentFacility && !currentServiceArea && (
                <Check className="h-4 w-4 shrink-0 text-statura-green" />
              )}
            </button>
          )}

          {hasRegions ? (
            <>
              {/* Grouped by region */}
              {sortedRegionIds.map((regionId) => {
                const region = regionMap.get(regionId);
                const regionFacilities = facilitiesByRegion.get(regionId) ?? [];
                if (regionFacilities.length === 0) return null;
                return (
                  <div key={regionId}>
                    <div className="flex items-center gap-1.5 px-3 pt-3 pb-1">
                      <FolderTree className="h-3 w-3 text-text-muted" />
                      <p className="text-eyebrow uppercase tracking-wider text-text-muted">
                        {region?.name ?? 'Unknown Region'}
                      </p>
                    </div>
                    {regionFacilities.map((facility) => (
                      <FacilityButton
                        key={facility.id}
                        facility={facility}
                        isSelected={effectiveCurrentFacility?.id === facility.id}
                        placement={placement}
                        onSelect={() => {
                          selectFacility(facility);
                          setOpen(false);
                        }}
                      />
                    ))}
                  </div>
                );
              })}

              {/* Ungrouped facilities (no region) */}
              {(facilitiesByRegion.get(null) ?? []).length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 px-3 pt-3 pb-1">
                    <Building2 className="h-3 w-3 text-text-muted" />
                    <p className="text-eyebrow uppercase tracking-wider text-text-muted">
                      Other {locations}
                    </p>
                  </div>
                  {(facilitiesByRegion.get(null) ?? []).map((facility) => (
                    <FacilityButton
                      key={facility.id}
                      facility={facility}
                      isSelected={effectiveCurrentFacility?.id === facility.id}
                      placement={placement}
                      onSelect={() => {
                        selectFacility(facility);
                        setOpen(false);
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            /* Flat list — backward compatible */
            selectableFacilities.map((facility) => (
              <FacilityButton
                key={facility.id}
                facility={facility}
                isSelected={effectiveCurrentFacility?.id === facility.id}
                placement={placement}
                onSelect={() => {
                  selectFacility(facility);
                  setOpen(false);
                }}
              />
            ))
          )}

          {selectableServiceAreas.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 px-3 pt-3 pb-1">
                <MapPin className="h-3 w-3 text-text-muted" />
                <p className="text-eyebrow uppercase tracking-wider text-text-muted">
                  Support at Home branches
                </p>
              </div>
              {selectableServiceAreas.map((serviceArea) => (
                <ServiceAreaButton
                  key={`service-area-${serviceArea.id}`}
                  serviceArea={serviceArea}
                  isSelected={currentServiceArea?.id === serviceArea.id}
                  placement={placement}
                  onSelect={() => {
                    selectServiceArea(serviceArea);
                    setOpen(false);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
